import { Injectable } from '@angular/core';
import { registerSentryUserBasket } from '@epicuro-next/platform/sentry';
import { go, selectRouteParams } from '@epicuro-next/state/router-store';
import { BASKET_ID, patchSession } from '@epicuro-next/state/session-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { BasketStatus } from 'graphql-api/baskets-api-interfaces';
import { concat, interval, NEVER, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { CBasketService } from '../../services';
import * as basketActions from '../actions';
import {
  loadDtoBasketSuccess,
  pollBasket,
  setActiveBasketId,
} from '../actions';
import {
  selectActiveBasketId,
  selectActiveBasketSummary,
  selectBasketPollingRule,
  selectGroupedBasketData,
  selectIsActiveBasketAndOpenedBasketDifferent,
  selectOpenedBasketId,
} from '../selectors';
import { logout } from '@epicuro-next/state/user-state';

/**
 * Basket state side effects
 */
@Injectable()
export class BasketInitEffects {
  private stopPooling$ = new Subject<void>();

  public loadDtoBasketById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.loadBasketById),
      debounceTime(0),
      concatLatestFrom(() => this.store.pipe(select(selectOpenedBasketId))),
      filter(([, id]) => Boolean(id)),
      switchMap(([, id]) =>
        this.basketService.getDtoBasket(id).pipe(
          map((dtoBasket) =>
            basketActions.loadDtoBasketSuccess({ basket: dtoBasket }),
          ),
          catchError((error) =>
            of(basketActions.loadDtoBasketFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public loadOpenedDtoBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.loadOpenedBasket),
      switchMap(({ basketId }) =>
        this.basketService.getDtoBasket(basketId).pipe(
          switchMap((data) => [
            basketActions.loadDtoBasketSuccess({ basket: data }),
            basketActions.loadBasketAccounts({ basketId }),
          ]),
          catchError((error) =>
            of(basketActions.loadDtoBasketFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  /** handle basket load fails
   * also this handle access for manipulating opened basket as BE returns error if a User has no rights for requested basket
   */
  public handleLoadBasketFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        basketActions.loadBasketFailure,
        basketActions.loadDtoBasketFailure,
        basketActions.groupDtoBasketFailure,
      ),
      // fix for selectRouteParams is not undefined
      debounceTime(0),
      concatLatestFrom(() => this.store.pipe(select(selectRouteParams))),
      filter(([_, routeParams]) => Boolean(routeParams?.basketId)),
      switchMap(() => [
        go({
          commands: ['/'],
          extras: {
            state: {
              forceLeaveBasket: true,
            },
          },
        }),
        showSnackBarMessage({
          message: $localize`:Basket|Failed to load basket message@@BasketLoadFail:Failed to load basket.`,
          messageType: MessageType.DANGER,
        }),
      ]),
    ),
  );

  public loadActiveDtoBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.loadActiveBasket),
      debounceTime(0),
      concatLatestFrom(() => [this.store.pipe(select(selectActiveBasketId))]),
      mergeMap(([_, activeBasketId]) => {
        if (isNullish(activeBasketId)) return NEVER;

        return this.basketService.getDtoBasket(activeBasketId).pipe(
          map((data) => basketActions.loadDtoBasketSuccess({ basket: data })),
          catchError((error) =>
            concat([
              basketActions.loadActiveBasketFailure({ error: error.message }),
              basketActions.loadDtoBasketFailure({ error: error.message }),
            ]),
          ),
        );
      }),
    ),
  );

  public patchSessionWithBasketId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.setActiveBasketId),
      map(({ basketId }) => {
        return patchSession({
          session: {
            [BASKET_ID]: basketId,
          },
        });
      }),
    ),
  );

  public pollBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pollBasket),
      concatLatestFrom(() => [
        this.store.pipe(select(selectActiveBasketSummary)),
        this.store.pipe(select(selectGroupedBasketData)),
        this.store.pipe(select(selectIsActiveBasketAndOpenedBasketDifferent)),
      ]),
      switchMap(([, activeBasket, openedBasket, areBasketsDifferent]) => {
        const basketsToPoll = areBasketsDifferent
          ? [activeBasket, openedBasket]
          : [activeBasket];

        return basketsToPoll.filter(
          (basket) => basket?.status === BasketStatus.New,
        );
      }),
      mergeMap((basket) => {
        if (!basket) return NEVER;

        return this.basketService.pollOnceDtoBasket(
          basket.basketId,
          basket.etag,
        );
      }),
      filter((basketDto) => !!basketDto),
      map((basketDto) => loadDtoBasketSuccess({ basket: basketDto! })),
      catchError((err) => {
        console.warn(err);

        return NEVER;
      }),
    ),
  );

  public initBasketPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDtoBasketSuccess, setActiveBasketId),
      concatLatestFrom(() => this.store.pipe(select(selectBasketPollingRule))),
      filter(([, isPollingEnabled]) => Boolean(isPollingEnabled)),
      switchMap(() =>
        interval(10000).pipe(
          map(() => pollBasket()),
          takeUntil(this.stopPooling$),
        ),
      ),
    ),
  );

  public stopBasketPolling$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        tap(() => {
          this.stopPooling$.next();
        }),
      ),
    { dispatch: false },
  );

  public registerSentryUserBasket$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(basketActions.setActiveBasketId),
        tap(({ basketId }) => registerSentryUserBasket(basketId)),
      ),
    { dispatch: false },
  );

  /** Dependencies */
  constructor(
    private actions$: Actions,
    private basketService: CBasketService,
    private store: Store,
  ) {}
}
